import { useEffect, useState } from 'react';
import { useAuthStore } from '../../../stores/authStore';
import { useMembershipStore } from '../../../stores/membershipStore';
import { MembershipStatusCard } from './membership/MembershipStatusCard';
import { FeatureCompare } from './membership/FeatureCompare';
import { UpgradeHistory } from './membership/UpgradeHistory';
import { InvitePanel } from './membership/InvitePanel';
import { CheckoutDialog } from './membership/CheckoutDialog';
import { useMembershipData } from './membership/useMembershipData';

interface Props {
  /** deep-link：?checkout=1 时自动打开结算弹窗 */
  autoCheckout?: boolean;
  /** deep-link：?plan=CODE 预选套餐 */
  initialPlanCode?: string | null;
  /** deep-link：?ref=CODE 预填邀请码（同时由全局捕获完成绑定） */
  initialReferralCode?: string | null;
}

/**
 * 会员面板（设置页）——由多个可复用子组件组合而成：
 * - MembershipStatusCard：当前等级/到期/自动续费/升级入口（服务端驱动）
 * - FeatureCompare：等级功能对比（等级列由后端动态下发）
 * - InvitePanel：邀请码分享 + 累计统计 + 邀请记录
 * - UpgradeHistory：升级/续费记录
 * - CheckoutDialog：套餐选择 + 下单 + 支付（跳转/扫码/转账/模拟）
 *
 * 会员状态统一走 membershipStore（GET /membership/status），支付成功后刷新状态与订单。
 */
export default function MembershipPanel({ autoCheckout, initialPlanCode, initialReferralCode }: Props) {
  const { refreshProfile } = useAuthStore();
  const refreshMembership = useMembershipStore((s) => s.refresh);
  const { compareData, plans, plansFailed, channels, orders, isLoading, refetchAfterPayment } = useMembershipData();

  const [showModal, setShowModal] = useState(false);
  const [doneMsg, setDoneMsg] = useState('');

  // deep-link：?checkout=1 自动打开结算弹窗（套餐/邀请码由 CheckoutDialog 预选预填）
  useEffect(() => {
    if (autoCheckout) setShowModal(true);
  }, [autoCheckout]);

  // 支付成功后的统一刷新：会员状态 + 订单/渠道 + authStore.profile（供仍读 profile 的组件同步）
  const handlePaid = async () => {
    setShowModal(false);
    setDoneMsg('支付成功！会员已激活');
    await refreshMembership();
    refetchAfterPayment();
    await refreshProfile();
  };

  // 支付宝同步跳转返回：检测 ?payResult=&outTradeNo= 参数
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payResult = params.get('payResult');
    const outTradeNo = params.get('outTradeNo');
    if (payResult && outTradeNo) {
      if (payResult === 'success') {
        setDoneMsg('支付成功！会员已激活');
        void refreshMembership();
        refetchAfterPayment();
        void refreshProfile();
      } else {
        setDoneMsg('支付结果待确认，如已支付请稍等片刻后刷新');
      }
      window.history.replaceState({}, '', window.location.pathname);
    }
    // 仅在挂载时处理一次同步跳转参数
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return <div className="p-6 text-center text-gray-500">加载中…</div>;
  }

  return (
    <div className="space-y-6">
      <MembershipStatusCard onUpgrade={() => setShowModal(true)} doneMsg={showModal ? '' : doneMsg} />

      <FeatureCompare compareData={compareData} />

      <InvitePanel />

      <UpgradeHistory orders={orders} />

      <CheckoutDialog
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setDoneMsg('');
        }}
        plans={plans}
        plansFailed={plansFailed}
        channels={channels}
        onPaid={handlePaid}
        initialPlanCode={initialPlanCode ?? undefined}
        initialReferralCode={initialReferralCode ?? undefined}
      />
    </div>
  );
}
